import { NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Pipe,
  PipeTransform,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  Metric,
  MetricFormatPipe,
  MetricType,
  ROAS,
  TARGET_ACOS,
} from "@front/m19-metrics";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";
import { AccountSelectionService, groupBy, UserSelectionService } from "@front/m19-services";
import {
  IButtonDirective,
  IInputGroupDirective,
  IInputGroupInput,
  ITooltipDirective,
  ModalService,
  IIconComponent,
} from "@front/m19-ui";
import { mergeSeveralDates } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { MetricRelativeValuePipe, StrategyStats } from "@m19-board/models/Metric";
import { CsvExportService, fieldExtractor, metricField, simpleField } from "@m19-board/services/csv-export.service";
import { StrategyLinkComponent } from "@m19-board/shared/strategy-link/strategy-link.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { DayPartingPopUpComponent, DayPartingPopUpData } from "../day-parting-pop-up/day-parting-pop-up.component";
import { DELETED, NOT_OPERATED, StrategyIdOrNotOperated } from "../hourly-page/hourly-page.component";

@Pipe({ name: "removePercentPipe", standalone: true })
export class RemovePercentPipe implements PipeTransform {
  transform(value: string): string {
    return value.replace("%", "");
  }
}

@Pipe({ name: "metricClassBucketPipe", standalone: true })
export class MetricClassBucketPipe<T> implements PipeTransform {
  transform(metric: Metric<T>, d: Map<string, T>, hour: string): string {
    const stats = d.get(hour);
    if (!stats) return "bg-gray-50";
    const val = metric.value(stats);
    if (val == undefined || !isFinite(val)) return "bg-gray-50";

    const level = metric.getGradientBucket(d, hour);
    switch (level) {
      case 0:
        return "bg-main-50";
      case 1:
        return "bg-main-100/50";
      case 2:
        return "bg-main-100";
      case 3:
        return "bg-main-200/50";
      case 4:
        return "bg-main-200";
      case 5:
        return "bg-main-300/50";
      case 6:
        return "bg-main-300";
      case 7:
        return "bg-main-400/80";
      case 8:
        return "bg-main-400";
      case 9:
        return "bg-main-500";
      default:
        return "bg-gray-50";
    }
  }
}

@Component({
  selector: "app-hourly-table",
  imports: [
    MetricClassBucketPipe,
    NgClass,
    StrategyLinkComponent,
    MetricFormatPipe,
    ITooltipDirective,
    GridLayoutComponent,
    TranslocoDirective,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    ExportButtonComponent,
    IButtonDirective,
    MetricRelativeValuePipe,
    RemovePercentPipe,
    IIconComponent,
  ],
  templateUrl: "./hourly-table.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HourlyTableComponent {
  protected readonly modalService = inject(ModalService);
  protected readonly translocoService = inject(TranslocoService);
  protected readonly accountSelectionService = inject(AccountSelectionService);
  protected readonly csvExportService = inject(CsvExportService);
  protected readonly userSelectionService = inject(UserSelectionService);

  readonly HOURS = Array.from(Array(24).keys()).map((x) => x.toString());
  readonly CELL_SIZE_CLASS = "h-[40px] w-[55px]";
  readonly MetricType = MetricType;

  readonly currency = input.required<string>();
  readonly metric = input.required<Metric<StrategyStats>>();
  readonly locale = input.required<string>();
  readonly strategies = input<SpStrategyEx[]>([]);
  readonly avgByHour = input.required<Map<number | "NOT_OPERATED", StrategyStats[]>>();

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: true });

  private readonly strategyMapByKey = computed(() => new Map(this.strategies().map((s) => [s.strategyId!, s])));

  private readonly avgStatsByStrategyByHour = computed(() => {
    const res = new Map<number | "NOT_OPERATED", Map<string, StrategyStats>>();
    this.avgByHour().forEach((value, key) => {
      res.set(
        key,
        groupBy(
          value,
          (x) => x.hour!.toString(),
          (a, _) => a,
        ),
      );
    });
    return res;
  });

  readonly datasource = computed(() => this.avgStatsByStrategyByHour());

  readonly totalByStrategy = computed(() => {
    const res = new Map<number | "NOT_OPERATED", StrategyStats>();
    for (const [strategyId, statsByHour] of this.avgStatsByStrategyByHour()) {
      res.set(
        strategyId,
        statsByHour.values().reduce((acc, curr) => mergeSeveralDates(acc, curr), {}) as StrategyStats,
      );
    }
    return res;
  });

  readonly dayPartingByStrategy = computed(() => {
    const res = new Map<number | "NOT_OPERATED", { start: number; end: number }>();
    for (const [strategyId, statsByHour] of this.avgStatsByStrategyByHour()) {
      if (statsByHour.size === 0) continue;
      const stat = Array.from(statsByHour.values())[0];
      if (!stat || stat.dayPartingPauseHour == null || stat.dayPartingReactivationHour == null) continue;
      res.set(strategyId, { start: stat.dayPartingPauseHour, end: stat.dayPartingReactivationHour });
    }
    return res;
  });

  readonly tableRows = computed(() => {
    return Array.from(this.avgStatsByStrategyByHour().keys())
      .map((id) =>
        id === NOT_OPERATED ? NOT_OPERATED : (this.strategyMapByKey().get(id) ?? { name: DELETED, strategyId: id }),
      )
      .filter(
        (x) =>
          this.textFilter() === "" ||
          (x !== NOT_OPERATED && x.name !== DELETED && x.name.toLowerCase().includes(this.textFilter().toLowerCase())),
      );
  });

  readonly textFilter = signal("");
  readonly showAbsValues = signal(true);

  onCellClick(strategyId: StrategyIdOrNotOperated, hour: number): void {
    if (strategyId === NOT_OPERATED || this.isReadOnly()) return;
    const strategy = this.strategyMapByKey().get(strategyId);
    if (!strategy) return;
    this.openDayPartingModal(strategy, hour);
  }

  private openDayPartingModal(strategy: SpStrategyEx, startHour: number) {
    this.modalService.openModal<DayPartingPopUpData, void>(DayPartingPopUpComponent, {
      modalTitle: this.translocoService.translate("day-parting-pop-up.stategy_dayparting", [strategy.name]),
      data: {
        strategy: strategy,
        organizationId: this.accountMarketplace().resourceOrganizationId,
        currencyCode: this.currency(),
        locale: this.locale(),
        dayPartingPauseHour: startHour,
        dayPartingReactivationHour: startHour + 1,
      },
    });
  }

  downloadFile() {
    const data = Array.from(this.avgStatsByStrategyByHour().values()).flatMap((x: Map<string, StrategyStats>) =>
      Array.from(x.values()),
    );

    this.csvExportService.exportCsv<StrategyStats>(
      {
        prefix: "strategy_hourly_stats",
        accountGroupName: this.accountMarketplace().accountGroupName,
        marketplace: this.accountMarketplace().marketplace,
        dateRange: this.userSelectionService.getDateRangeStr(),
      },
      data.filter((x) => !!x.accountId),
      [
        fieldExtractor("strategyId", (d) => d.strategyId?.toString() ?? "-"),
        simpleField("strategyName"),
        simpleField("hour"),
        simpleField("currency"),
        metricField(CLICKS),
        metricField(COST),
        metricField(AD_CONVERSIONS),
        metricField(AD_SALES),
        metricField(ACOS),
        metricField(TARGET_ACOS),
        metricField(CONVERSION_RATE),
        metricField(ROAS),
        metricField(CPC),
      ],
    );
  }
}
